import { useState } from 'react'
import { Link } from 'react-router-dom'
import { APP_NAME } from '../config/brand'
import { AuthLayout } from '../layouts/AuthLayout'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { requestPasswordReset } from '../services/authService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const RESET_EMAIL_STORAGE_KEY = 'shopora_business_admin_reset_email'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  async function submitResetRequest() {
    setError(null)
    const trimmed = email.trim()
    if (!trimmed) {
      setError('Please enter your email address.')
      return
    }
    if (!EMAIL_RE.test(trimmed)) {
      setError('Enter a valid email address.')
      return
    }

    setSubmitting(true)
    try {
      await requestPasswordReset(trimmed)
      sessionStorage.setItem(RESET_EMAIL_STORAGE_KEY, trimmed)
      setSent(true)
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'Could not send reset code.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    await submitResetRequest()
  }

  const brandInitial = APP_NAME.charAt(0).toUpperCase()

  return (
    <AuthLayout>
      <Card className="w-full max-w-md shadow-2xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-lg font-bold text-primary-foreground shadow-lg">
            {brandInitial}
          </div>
          <h1 className="text-xl font-semibold text-foreground">
            {sent ? 'Check your inbox' : 'Forgot password?'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {sent
              ? 'If an account exists for that email, we sent password reset instructions.'
              : `Enter your ${APP_NAME} platform admin email and we will email you a 6-digit reset code.`}
          </p>
        </div>

        {sent ? (
          <div className="space-y-4">
            <p
              className="rounded-xl border border-border bg-muted/40 px-3 py-3 text-sm text-foreground"
              role="status"
            >
              Look for an email with a 6-digit reset code. It expires in about 15 minutes.
            </p>
            <Link
              to="/reset-password"
              state={{ email: email.trim() }}
              className="inline-flex w-full items-center justify-center rounded-2xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground shadow-sm transition-all hover:bg-muted hover:shadow-md active:scale-[0.98]"
            >
              Enter reset code
            </Link>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              disabled={submitting}
              onClick={() => void submitResetRequest()}
            >
              {submitting ? 'Resending…' : 'Resend email'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
                Back to sign in
              </Link>
            </p>
          </div>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" noValidate>
            <Input
              id="email"
              type="email"
              label="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {error ? (
              <p
                className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                role="alert"
              >
                {error}
              </p>
            ) : null}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send reset code'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </Card>
    </AuthLayout>
  )
}
